import { compile, parse, type DialStep } from "@/features/phone-call-widget/dialSequence";

/**
 * `capped` has no default on purpose: the 32-char cap is real for staff destinations (Twilio
 * `sendDigits` on a live call) but wrong for caller-ID verification (Twilio `validationRequests`
 * has no such limit — see the comment on `extensionSchema` in `API-Server/app/domain/phoneSchemas.ts`).
 * A future call site that forgets this argument must fail to compile, not silently inherit
 * whichever rule happened to be the default.
 */
export const isExtensionValid = (value: string, opts: { capped: boolean }): boolean => {
    // EN-1051: trim surrounding whitespace before validating. A trailing space is invisible in the
    // input, so it silently failed the charset test and made the (accurate) "digits, *, # and
    // commas only" message read as wrong. Internal spaces stay invalid on purpose — see the
    // "1 2" case in __tests__/callerIdExtension.test.ts.
    const trimmed = value.trim();
    if (trimmed === "") return true;
    if (!/^[0-9*#,]+$/.test(trimmed) || !/[0-9*#]/.test(trimmed)) return false;
    if (!opts.capped) return true;
    // Twilio sendDigits caps at 32 chars; each comma expands to 'wwww' on the backend. This cap is
    // staff-destination-only — see the doc comment above.
    return trimmed.replace(/,/g, "wwww").length <= 32;
};

/**
 * Mirrors the digit-or-symbol half of the server's `baseExtensionSchema` refinement (`API-Server/app/domain/phoneSchemas.ts` —
 * `.refine(v => /[0-9*#]/.test(v))`). A compiled dial sequence needs at least one dialable
 * character to be a legal `extension` value on the wire; an all-comma string (a wait-only
 * `DialSequenceField` sequence, e.g. `compile([{kind:"wait",seconds:6}])` === `",,,"`) fails that
 * server-side check with a 400 — BEFORE the server's `buildDialColumns` ever gets a chance to
 * ignore the client-supplied `extension` in favor of `dial_steps` (schema validation runs first,
 * in `phoneRoutes.ts`; the "ignore it" substitution happens later, inside the controller). So a
 * caller-ID form sending both `dial_steps` and a digit-less `extension` must omit `extension`
 * from the request body entirely — not send it and rely on the server to override it, which never
 * gets that far. Callers gate `extension` inclusion on this rather than dropping it whenever
 * `dial_steps` is present, so every OTHER sequence (i.e. every one with a digit) still sends both,
 * preserving back-compat with an older server that ignores the unknown `dial_steps` key.
 */
export const hasDialableCharacter = (value: string): boolean => /[0-9*#]/.test(value);

/**
 * The single place that turns the step builder's `DialStep[]` into the caller-ID request's
 * dial-related keys. Both caller-ID entry points (`CallerIdNumberForm` in the widget,
 * `CallerIdVerificationModal` in settings) submit the identical recipe, so it lives here rather
 * than being written out at each of them. (Unlike the frontend/backend `compile()` pair, which is
 * deliberately duplicated — see dialSequence.ts.)
 *
 * Two things this deliberately does NOT do, both of which look like omissions:
 *
 *  - It does not gate on `extension` being non-empty before calling `hasDialableCharacter`. The
 *    call sites used to read `extension && hasDialableCharacter(extension)`; the first conjunct is
 *    dead, because `hasDialableCharacter` is `/[0-9*#]/.test(v)`, which cannot match `""`.
 *  - It does not build the whole mutation argument (phone number, country code, reference name).
 *    Those genuinely differ between the two call sites — the widget form falls back to
 *    `"Phone Number"` when the vet leaves the name blank, the settings modal does not — so
 *    folding them in would mean a parameter whose only job is to re-introduce the difference.
 *
 * The `dialSteps.length > 0` gate is belt-and-braces: `addCallerId` in `usePhoneCall.ts` re-checks
 * it before writing `dial_steps` onto the wire, because omitting the key is what signals "clear
 * the stored sequence" on retry. Keeping it here too means this helper's return value is honest on
 * its own rather than only in combination with its one consumer.
 */
export const callerIdDialPayload = (dialSteps: DialStep[]): { extension?: string; dialSteps?: DialStep[] } => {
    const extension = compile(dialSteps);
    return {
        // A wait-only compiled extension (e.g. ",,,") is a legal DialSequence but an illegal wire
        // `extension` — see the doc comment on hasDialableCharacter above for why it must be
        // omitted rather than sent and overridden server-side.
        ...(hasDialableCharacter(extension) && { extension }),
        ...(dialSteps.length > 0 && { dialSteps }),
    };
};

/**
 * EN-1050: the read-side counterpart to `callerIdDialPayload` — the one derivation that turns a
 * stored caller-ID row back into steps. Prefer the structured `dialSteps` when the row has them;
 * fall back to parsing the compiled `extension` for legacy rows written before the column existed.
 *
 * One function for the same reason `callerIdDialPayload` is: `CallSettings`,
 * `CallerIdVerificationModal` and `CallerIdNumberForm` all need `X.dialSteps ?? parse(X.extension
 * ?? "")`, and a fourth hand-rolled copy is how the "one shared derivation rule" claim stops being
 * true.
 *
 * `??` and not a length check, deliberately: an empty `dialSteps` array cannot coexist with a
 * non-empty legacy `extension`. The server writes both columns from the same steps in one statement
 * (`buildDialColumns`, API-Server `app/domain/phoneSchemas.ts`, returns `extension: undefined` for
 * `[]` because `compile([])` is `""`), so `dialSteps === []` implies `extension IS NULL` and
 * returning `[]` is correct rather than a hidden legacy sequence. `[]` also carries the "clear the
 * stored sequence" meaning on retry, so it must not be treated as absent.
 */
export const callerIdDialSteps = (row: { dialSteps?: DialStep[] | null; extension?: string | null }): DialStep[] =>
    row.dialSteps ?? parse(row.extension ?? "");
